/**
 * Reusable line-item editor for Quotation / Sales Invoice / Purchase Invoice.
 * Computes GST client-side; backend re-computes on save so we can trust display.
 */
import React from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Trash, Plus } from "@phosphor-icons/react";
import { inr } from "@/lib/api";

export function computeLine(it, intraState, gstEnabled = true) {
  const gross = (Math.max(1, Math.floor(Number(it.quantity) || 0))) * (Number(it.rate) || 0);
  const discount = gross * ((Number(it.discount_pct) || 0) / 100);
  const taxable = gross - discount;
  const rate = gstEnabled ? (Number(it.tax_rate) || 0) : 0;
  const taxAmt = taxable * (rate / 100);
  const cgst = intraState ? taxAmt / 2 : 0;
  const sgst = intraState ? taxAmt / 2 : 0;
  const igst = intraState ? 0 : taxAmt;
  return { taxable_value: taxable, cgst, sgst, igst, total: taxable + taxAmt };
}

export function computeTotals(items, intraState, gstEnabled = true) {
  let subtotal = 0, cgst = 0, sgst = 0, igst = 0;
  items.forEach((it) => {
    const c = computeLine(it, intraState, gstEnabled);
    subtotal += c.taxable_value;
    cgst += c.cgst; sgst += c.sgst; igst += c.igst;
  });
  return { subtotal, cgst_total: cgst, sgst_total: sgst, igst_total: igst, grand_total: subtotal + cgst + sgst + igst };
}

export default function LineItemsEditor({ items, setItems, products = [], intraState = true, gstEnabled = true }) {
  const addLine = () => setItems([...items, {
    product_id: "", name: "", hsn: "", quantity: 1, unit: "PCS",
    rate: 0, discount_pct: 0, tax_rate: gstEnabled ? 18 : 0,
  }]);

  const removeLine = (i) => setItems(items.filter((_, idx) => idx !== i));

  const updateLine = (i, patch) => setItems(items.map((it, idx) => idx === i ? { ...it, ...patch } : it));

  const pickProduct = (i, productId) => {
    const p = products.find((x) => x.id === productId);
    if (!p) return;
    updateLine(i, {
      product_id: p.id, name: p.name, hsn: p.hsn || "",
      unit: p.unit || "PCS", rate: p.sale_price || 0,
      tax_rate: gstEnabled ? (p.tax_rate || 18) : 0,
    });
  };

  return (
    <div className="card-flat overflow-x-auto" data-testid="line-items-editor">
      <table className="data-table min-w-[1100px]">
        <thead>
          <tr>
            <th style={{ width: "28%" }}>Item / Product</th>
            {gstEnabled && <th style={{ width: "9%" }}>HSN</th>}
            <th className="num" style={{ width: "9%" }}>Qty</th>
            <th className="num" style={{ width: "11%" }}>Rate</th>
            <th className="num" style={{ width: "8%" }}>Disc %</th>
            {gstEnabled && <th className="num" style={{ width: "8%" }}>Tax %</th>}
            <th className="num" style={{ width: "12%" }}>{gstEnabled ? "Taxable" : "Amount"}</th>
            <th className="num" style={{ width: "12%" }}>Total</th>
            <th style={{ width: "3%" }}></th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && (
            <tr><td colSpan={gstEnabled ? 9 : 7} className="text-center text-muted-foreground py-6">No items. Add a line to begin.</td></tr>
          )}
          {items.map((it, i) => {
            // Stable per-row key so typing into the "Item name" field doesn't
            // remount the row (which caused instant focus-loss on every
            // keystroke — bug fixed 2026-02-28). When the row is linked to a
            // master product/service, we keep that id; otherwise the row's
            // index is stable enough because we only add (append) or delete
            // (filter) — we never reorder lines.
            const rowKey = it.product_id || `row-${i}`;
            const c = computeLine(it, intraState, gstEnabled);
            return (
              <tr key={rowKey}>
                <td>
                  <div className="flex flex-col gap-1.5">
                    {products.length > 0 && (
                      <select
                        className="h-7 rounded-md border border-input bg-transparent px-2 text-[11px] w-full"
                        value={it.product_id || ""}
                        onChange={(e) => pickProduct(i, e.target.value)}
                        data-testid={`item-product-${i}`}
                      >
                        <option value="">— Custom (type name below) —</option>
                        {products.filter((p) => p._kind !== "service").length > 0 && (
                          <optgroup label="Products">
                            {products.filter((p) => p._kind !== "service").map((p) => (
                              <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                          </optgroup>
                        )}
                        {products.filter((p) => p._kind === "service").length > 0 && (
                          <optgroup label="Services">
                            {products.filter((p) => p._kind === "service").map((p) => (
                              <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                          </optgroup>
                        )}
                      </select>
                    )}
                    <Input value={it.name} onChange={(e) => updateLine(i, { name: e.target.value })}
                      className="h-8 w-full" placeholder="Item name" data-testid={`item-name-${i}`} />
                  </div>
                </td>
                {gstEnabled && (
                  <td>
                    <Input value={it.hsn} onChange={(e) => updateLine(i, { hsn: e.target.value })}
                      className="h-8 font-mono text-[12px] min-w-[80px]" />
                  </td>
                )}
                <td className="num">
                  <Input
                    type="number"
                    step="1"
                    min="1"
                    inputMode="numeric"
                    value={it.quantity}
                    onChange={(e) => {
                      const raw = e.target.value;
                      const n = raw === "" ? "" : Math.max(1, Math.floor(Number(raw) || 0));
                      updateLine(i, { quantity: n });
                    }}
                    onKeyDown={(e) => {
                      if (["."," ","e","E","+","-"].includes(e.key)) e.preventDefault();
                    }}
                    onWheel={(e) => e.target.blur()}
                    className="h-8 text-right font-mono min-w-[80px]"
                    data-testid={`item-qty-${i}`}
                  />
                </td>
                <td className="num">
                  <Input type="number" step="0.01" value={it.rate} onChange={(e) => updateLine(i, { rate: e.target.value })}
                    className="h-8 text-right font-mono min-w-[100px]" />
                </td>
                <td className="num">
                  <Input type="number" step="0.01" value={it.discount_pct} onChange={(e) => updateLine(i, { discount_pct: e.target.value })}
                    className="h-8 text-right font-mono min-w-[80px]" />
                </td>
                {gstEnabled && (
                  <td className="num">
                    <Input type="number" step="0.01" value={it.tax_rate} onChange={(e) => updateLine(i, { tax_rate: e.target.value })}
                      className="h-8 text-right font-mono min-w-[80px]" />
                  </td>
                )}
                <td className="num whitespace-nowrap">{inr(c.taxable_value)}</td>
                <td className="num font-semibold whitespace-nowrap">{inr(c.total)}</td>
                <td>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive"
                    onClick={() => removeLine(i)} data-testid={`item-del-${i}`}>
                    <Trash size={14} />
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="p-3 border-t border-border">
        <Button variant="outline" onClick={addLine} className="h-8" type="button" data-testid="add-line-btn">
          <Plus size={14} className="mr-1.5" weight="bold" /> Add Line
        </Button>
      </div>
    </div>
  );
}
